import React from 'react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeaturePermission, FEATURE_PERMISSIONS } from '../../utils/permissions';

/**
 * PermissionButton - A button that respects feature permissions
 * 
 * @param {string} permission - The permission key from FEATURE_PERMISSIONS
 * @param {string} tooltipText - Tooltip text shown when permission is denied
 * @param {boolean} hideWhenDenied - If true, completely hide the button instead of disabling
 * @param {React.ReactNode} children - Button content
 * @param {object} props - All other Button props
 */
export const PermissionButton = ({
  permission,
  tooltipText = "You don't have permission to perform this action",
  hideWhenDenied = false,
  children,
  ...props
}) => {
  const { user } = useAuth();
  const hasPermission = hasFeaturePermission(user, permission);

  // If hideWhenDenied is true and no permission, don't render anything
  if (!hasPermission && hideWhenDenied) {
    return null;
  }

  // If no permission, render disabled button with tooltip
  if (!hasPermission) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                {...props}
                disabled
                className={`${props.className || ''} opacity-50 cursor-not-allowed`}
              >
                <Lock className="h-3 w-3 mr-1" />
                {children}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Has permission - render normal button
  return (
    <Button {...props}>
      {children}
    </Button>
  );
};

/**
 * PermissionGate - Wrapper component that shows/hides children based on permission
 * 
 * @param {string} permission - The permission key from FEATURE_PERMISSIONS
 * @param {React.ReactNode} children - Content to show if permitted
 * @param {React.ReactNode} fallback - Content to show if not permitted (optional)
 */
export const PermissionGate = ({
  permission,
  children,
  fallback = null
}) => {
  const { user } = useAuth();
  const hasPermission = hasFeaturePermission(user, permission);

  return hasPermission ? children : fallback;
};

/**
 * usePermission - Hook to check a permission
 * 
 * @param {string} permission - The permission key from FEATURE_PERMISSIONS
 * @returns {boolean} - Whether the user has the permission
 */
export const usePermission = (permission) => {
  const { user } = useAuth();
  return hasFeaturePermission(user, permission);
};

/**
 * usePermissions - Hook to check multiple permissions at once
 * 
 * @param {string[]} permissions - Array of permission keys
 * @returns {object} - Object mapping permission keys to boolean values
 */
export const usePermissions = (permissions) => {
  const { user } = useAuth();
  return permissions.reduce((acc, perm) => {
    acc[perm] = hasFeaturePermission(user, perm);
    return acc;
  }, {});
};

// Re-export FEATURE_PERMISSIONS for convenience
export { FEATURE_PERMISSIONS };
